import React, { useEffect } from "react";
import { message, Typography } from "antd";
import { inject, observer } from "mobx-react";

import UserDrawer from "./UserDrawer";
import { fetchDedicatedNumbers, sendCreateInvoiceWithNumbers } from "service/adminService";
import CreateInvoiceModal from "./UsageDetailModal";
import { useUsageData } from "hooks/useUsageData";
import { useDashboardState } from "hooks/useDashboardState";
import UsageAccordion from "components/Admin/AdminUsageDetail/UsageAccordion";
import AnalyticsSection from "components/Admin/AdminUsageDetail/AnalyticsSection";
import AppButton from "components/AppButton/AppButton";
import MonthFilterMenu from "components/Admin/FilterMenu/MonthFilterMenu";

function AccordionDashboard({ adminGatewayStore, match }) {
  const { userId } = match.params;
  const urlParam = new URLSearchParams(window.location.search);
  const userName = urlParam.get("user");
  const pageTitle = userName ? `${decodeURIComponent(userName)} Usage Detail` : "Admin Usage Detail";

  const {
    month,
    year,
    setMonth,
    setYear,
    activeKeys,
    setActiveKeys,
    isModalVisible,
    setIsModalVisible,
    visibleDrawer,
    setVisibleDrawer,
    refreshLoading,
    setRefreshLoading,
    dedicatedNumbers,
    setDedicatedNumbers
  } = useDashboardState();

  const {
    loading,
    adminTableLoading,
    dedicatedNumbersLoading,
    purchaseData,
    oldPurchaseData,
    smsStatusData,
    oldSmsStatusData,
    mmsStatusData,
    oldMmsStatusData,
    fetchAllData,
    fetchTablesData
  } = useUsageData(adminGatewayStore, userId, month, year);

  useEffect(() => {
    fetchAllData();
  }, [userId, year, month]);

  useEffect(() => {
    fetchTablesData();
  }, [month, year, userId]);

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      await Promise.all([fetchAllData(), fetchTablesData()]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setRefreshLoading(false);
    }
  };

  // Handle invoice creation
  const handleCreateInvoice = async formValues => {
    try {
      const apiParams = {
        month,
        year,
        userId,
        medium: "admin",
        ...formValues
      };

      await sendCreateInvoiceWithNumbers(apiParams);
      await adminGatewayStore.getUserUsageData(userId);

      message.success("Invoice created successfully");
      setIsModalVisible(false);
    } catch (e) {
      console.error("Error creating invoice:", e);
      message.error("Failed to create invoice");
    }
  };

  // Fetch dedicated numbers for modal
  const getDedicatedNumbersForModal = async () => {
    try {
      const data = await fetchDedicatedNumbers({
        page: 0,
        limit: 0,
        userId,
        type: "usage"
      });

      const filteredData = data.data.senderIds?.map(item => ({
        phone: item.number,
        amount: item.amount ? Number(item.amount) : 0,
        createdBy: item.createdBy
      }));

      setDedicatedNumbers(filteredData);
      return filteredData;
    } catch (error) {
      console.error("Failed to fetch dedicated numbers", error);
      return [];
    }
  };

  useEffect(() => {
    getDedicatedNumbersForModal();
  }, [userId]);

  // Safe access to purchase orders list with proper bounds checking
  const firstPurchaseOrder = adminGatewayStore.userPurchaseOrdersList?.length > 0 ? adminGatewayStore.userPurchaseOrdersList[0] : null;

  return (
    <div className="my-5 px-3">
      <div className="mb-4">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-4">
          <div className="flex-shrink-0">
            <Typography.Title level={4} className="mb-0">
              {pageTitle}
            </Typography.Title>
          </div>

          <div className="d-flex flex-column flex-sm-row justify-content-end align-items-stretch align-items-sm-center gap-2 w-100 w-lg-auto">
            <div className="order-1 flex-shrink-0">
              <MonthFilterMenu
                month={month}
                year={year}
                setMonth={setMonth}
                setYear={setYear}
                showExportButton={false}
                span={{
                  xs: 24,
                  sm: 12,
                  md: 8,
                  lg: 6,
                  xl: 4
                }}
              />
            </div>

            <div className="order-2 d-flex flex-column  flex-sm-row align-items-stretch align-items-sm-center gap-2">
              <AppButton type="primary" size="default" onClick={() => setIsModalVisible(true)} label="Create Invoice" light style={{ height: "32px" }} className="w-100 w-sm-auto" />

              <AppButton type="primary" size="default" onClick={() => setVisibleDrawer(true)} label="View Invoice" light style={{ height: "32px" }} className="w-100 w-sm-auto" />

              <AppButton
                type="primary"
                size="default"
                onClick={handleRefresh}
                light
                loading={refreshLoading}
                style={{ height: "32px" }}
                className="w-100 w-sm-auto d-flex justify-content-center align-items-center"
                label={
                  <span>
                    <i className="fas fa-sync-alt fs-5" />
                  </span>
                }
              />
            </div>
          </div>
        </div>
      </div>

      <UsageAccordion
        activeKeys={activeKeys}
        onCollapseChange={setActiveKeys}
        adminGatewayStore={adminGatewayStore}
        userId={userId}
        month={month}
        year={year}
        adminTableLoading={adminTableLoading}
        dedicatedNumbersLoading={dedicatedNumbersLoading}
        purchaseData={purchaseData}
        oldPurchaseData={oldPurchaseData}
      />

      <AnalyticsSection smsStatusData={smsStatusData} oldSmsStatusData={oldSmsStatusData} mmsStatusData={mmsStatusData} oldMmsStatusData={oldMmsStatusData} loading={loading} />

      <CreateInvoiceModal
        isModalVisible={isModalVisible}
        handleModalClose={() => setIsModalVisible(false)}
        onSubmit={handleCreateInvoice}
        dedicatedNumbers={dedicatedNumbers}
        totalSms={smsStatusData ? Number(smsStatusData?.totalSMS) - Number(smsStatusData?.totalSMSReceived) - Number(smsStatusData?.totalSMSBlocked) : 0}
        totalMms={mmsStatusData ? mmsStatusData?.totalSMS - mmsStatusData?.totalSMSReceived - mmsStatusData?.totalSMSBlocked : 0}
        rateSms={firstPurchaseOrder?.centsPerSMS || 0}
        rateMms={firstPurchaseOrder?.centsPerMMS || 0}
      />

      <UserDrawer visible={visibleDrawer} activeDrawer="viewInvoice" onClose={() => setVisibleDrawer(false)} userId={userId} />
    </div>
  );
}

export default inject(stores => ({
  adminGatewayStore: stores.store.adminGatewayStore
}))(observer(AccordionDashboard));
